import type { ConversationKind, UUID } from './types';

/** Private Supabase Realtime Broadcast topics (authorised by collab_rt.can_access_topic). */
export const topics = {
  conversation: (kind: ConversationKind, id: UUID) => (kind === 'channel' ? `collab:chat:channel:${id}` : `collab:chat:dm:${id}`),
  project: (id: UUID) => `collab:project:updates:${id}`,
  user: (id: UUID) => `collab:user:notifications:${id}`,
} as const;

/** Event names emitted by the API/worker. */
export const RealtimeEvents = {
  MessageCreated: 'message.created',
  MessageUpdated: 'message.updated',
  MessageDeleted: 'message.deleted',
  ReactionsChanged: 'message.reactions',
  PinsChanged: 'message.pins',
  Typing: 'typing',
  ReadState: 'read.updated',
  ConversationUpdated: 'conversation.updated',
  MemberAdded: 'conversation.member_added',
  /** t167u: lost access (left a linked department / group / building); clients refetch their conversations. */
  MemberRemoved: 'conversation.member_removed',
  /**
   * t722u: { workspace_id, conversation_id, is_private } a channel was made private or open. Sent to everyone in the
   * workspace who isn't in it (its members get conversation.updated): browse, search and an open channel page refetch,
   * and anyone looking at a channel that just became private leaves it.
   */
  ChannelVisibility: 'conversation.visibility',
  TaskCreated: 'task.created',
  TaskUpdated: 'task.updated',
  TaskDeleted: 'task.deleted',
  TaskCommented: 'task.commented',
  /** ids only; clients refetch chips through the permission-checked endpoint */
  TaskLinksChanged: 'task.links_changed',
  ProjectUpdated: 'project.updated',
  ChatSectionsChanged: 'chat_sections.changed',
  /** t471u: { workspace_id, user_id (the placeholder) } someone was deleted permanently; people lists and chats refetch. */
  MemberDeleted: 'workspace.member_deleted',
  Notification: 'notification.created',
  /** t162: { ids, entity_type?, entity_id? } marked read (any tab or device); clients refetch the bell. */
  NotificationsRead: 'notification.read',
  /**
   * t363u: some of this person's sign-in sessions were just ended (signed out everywhere, password changed, an admin
   * set a temporary password). Each device re-checks its own session: a live one refreshes, an ended one goes to sign in.
   */
  SessionRevoked: 'session.revoked',
  CallInvite: 'call.invite',
  CallUpdated: 'call.updated',
  ShiftsPublished: 'workforce.shifts_published.v1',
  WorkforceRequestUpdated: 'workforce.request_updated.v1',
  /** ids only; clients refetch the poll through the permission-checked endpoint */
  PollUpdated: 'poll.updated',
  AckUpdated: 'ack.updated',
  /** ids only (call_id, recording_id); clients refetch the recording through the permission-checked endpoint */
  RecordingUpdated: 'call.recording',
  /**
   * t187u: an item's reactions changed (feed cards: announcements, issues, events, shift offers, tasks):
   * { workspace_id, entity_type, entity_id, reactions, at }, sent only to people who can see the item.
   */
  ItemReactions: 'item.reactions',
} as const;
